import { v2Client } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";
import { LIVE_CHANNEL_IDS } from "@/v2/lib/liveChannels";
import type { AdminProgramRow, ApiBlock } from "./apiMapping";

export { LIVE_CHANNEL_IDS };

function authHeaders(): Record<string, string> {
  const token = getCurrentAuth()?.AccessToken;
  return token ? { Authorization: `bearer ${token}` } : {};
}

/** All channels' blocks for an inclusive date range (admin read model). */
export async function fetchSchedule(from: string, to: string): Promise<AdminProgramRow[]> {
  const res = await v2Client.get<{ data: { programs: AdminProgramRow[] } }>(
    APIRoutes.ChannelScheduleAdmin,
    { params: { from, to }, headers: authHeaders() },
  );
  return res.data?.data?.programs ?? [];
}

export interface BlockError {
  index: number;
  error: string;
}

/** Save failure carrying the backend's per-block validation errors. */
export class SaveDayError extends Error {
  blockErrors: BlockError[];
  constructor(message: string, blockErrors: BlockError[] = []) {
    super(message);
    this.name = "SaveDayError";
    this.blockErrors = blockErrors;
  }
}

export interface SaveDayResult {
  programs: AdminProgramRow[];
  version: number;
}

/** Validation Booms put blockErrors at the top level of the body, not
 * under data (see the backend's channelScheduleRoutes handle()). */
function toSaveError(e: any): SaveDayError {
  const body = e?.response?.data;
  return new SaveDayError(
    body?.message || e?.message || "Saving the schedule failed",
    Array.isArray(body?.blockErrors) ? body.blockErrors : [],
  );
}

/** Replace one channel-day. The response's rows carry the server ids. */
export async function saveChannelDay(
  channelId: number,
  date: string,
  blocks: ApiBlock[],
): Promise<SaveDayResult> {
  const path = APIRoutes.ChannelScheduleAdminDay.replace(
    ":channelId",
    String(channelId),
  ).replace(":date", date);
  try {
    const res = await v2Client.put<{ data: SaveDayResult }>(
      path,
      { programs: blocks },
      { headers: authHeaders() },
    );
    return res.data?.data ?? { programs: [], version: 0 };
  } catch (e: any) {
    throw toSaveError(e);
  }
}

const programsPath = (channelId: number, date: string) =>
  `${APIRoutes.ChannelScheduleAdminDay.replace(":channelId", String(channelId)).replace(":date", date)}/programs`;

/** A PATCH body: any subset of a block's fields; channelId/date move it. */
export type ProgramPatch = Partial<ApiBlock> & { channelId?: number; date?: string };

/** POST one block onto a channel-day; returns the created row. */
export async function createProgram(
  channelId: number,
  date: string,
  block: ApiBlock,
): Promise<AdminProgramRow> {
  try {
    const res = await v2Client.post<{ data: { program: AdminProgramRow } }>(
      programsPath(channelId, date),
      block,
      { headers: authHeaders() },
    );
    return res.data.data.program;
  } catch (e: any) {
    throw toSaveError(e);
  }
}

/** PATCH one block's changed fields (addressed at its current channel-day). */
export async function patchProgram(
  channelId: number,
  date: string,
  programId: number,
  fields: ProgramPatch,
): Promise<AdminProgramRow> {
  try {
    const res = await v2Client.patch<{ data: { program: AdminProgramRow } }>(
      `${programsPath(channelId, date)}/${programId}`,
      fields,
      { headers: authHeaders() },
    );
    return res.data.data.program;
  } catch (e: any) {
    throw toSaveError(e);
  }
}

/** A game's exact runtime, probed at drop time; null when unknown. */
export async function fetchGameDuration(gameId: string): Promise<number | null> {
  try {
    const res = await v2Client.get<{ data: { durationSec: number | null } }>(
      `${APIRoutes.ChannelScheduleAdmin}/duration/${gameId}`,
      { headers: authHeaders() },
    );
    return res.data?.data?.durationSec ?? null;
  } catch {
    return null;
  }
}

/** DELETE one block from a channel-day. */
export async function removeProgram(
  channelId: number,
  date: string,
  programId: number,
): Promise<void> {
  try {
    await v2Client.delete(`${programsPath(channelId, date)}/${programId}`, {
      headers: authHeaders(),
    });
  } catch (e: any) {
    throw toSaveError(e);
  }
}
